import { apiClient } from '@/shared/api';
import { buildAuthHeaders } from '@/shared/http/authHeaders';
import { API_ENDPOINTS, APP_CONFIG } from '@/config';
import type { LoginData, LoginResponse, PreLoginResponse } from '@/features/auth/types/auth.types';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { firebaseService } from '@/features/auth/services/firebaseService';
import { unwrapAuthTokens, unwrapPreLoginData } from '@/features/auth/utils/unwrapBffResponse';

const REFRESH_TOKEN_KEY = APP_CONFIG.session.refreshTokenKey;
const ACCESS_TOKEN_TTL = APP_CONFIG.session.accessTokenTtlSeconds;

function isMockAuthEnabled(): boolean {
  return (
    import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
    (import.meta.env as unknown as Record<string, string | undefined>).NEXT_PUBLIC_USE_MOCK_AUTH === 'true'
  );
}

function persistAuthTokens(data: LoginData): void {
  tokenStorage.setAccessToken(data.access_token);
  tokenStorage.setRefreshToken(data.refresh_token);
  tokenStorage.setAccessExpiresAt(data.expires_in);
}

export const authService = {
  async preLogin(): Promise<PreLoginResponse> {
    const newHeaders = buildAuthHeaders({
      withFirebaseSession: true,
      withLocation: true,
    });

    const raw = await apiClient.post<unknown>(API_ENDPOINTS.auth.preLogin, {}, { headers: newHeaders });
    const data = unwrapPreLoginData(raw);

    return {
      response: (raw as { response?: string })?.response ?? 'OK',
      data,
    };
  },

  async login(targetCustomerUuid?: string): Promise<LoginResponse> {
    const newHeaders = buildAuthHeaders({
      withFirebaseSession: true,
      withTempToken: true,
      withMfaToken: true,
      withLocation: true,
    });

    if (isMockAuthEnabled()) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockPayload = btoa(
            JSON.stringify({
              sub: '1773370393360440abb25-c310-4830-8931-0bcc50c9b787',
              type: 'access',
            })
          ).replace(/=/g, '');

          const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${mockPayload}.mock_signature`;

          resolve({
            data: {
              access_token: mockToken,
              refresh_token: 'the_refresh_token',
              expires_in: ACCESS_TOKEN_TTL,
              token_type: 'Bearer',
            },
            response: 'OK',
          });
        }, 100);
      });
    }

    const mfaToken = tokenStorage.getMfaTokenOptional();
    const raw = await apiClient.post<unknown>(
      API_ENDPOINTS.auth.login,
      {
        ...(mfaToken ? { mfa_token: mfaToken } : {}),
        ...(targetCustomerUuid ? { target_customer_uuid: targetCustomerUuid } : {}),
      },
      { headers: newHeaders }
    );

    const data = unwrapAuthTokens(raw);

    return {
      response: (raw as { response?: string })?.response ?? 'OK',
      data,
    };
  },

  async logout(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.auth.logout, {});
  },

  async switchCompany(targetCustomerUuid: string): Promise<LoginData> {
    const freshFirebaseToken = await firebaseService.getFreshIdToken();
    if (freshFirebaseToken) {
      tokenStorage.setFirebaseToken(freshFirebaseToken);
    }

    const newHeaders = buildAuthHeaders({
      withFirebaseSession: true,
      withLocation: true,
    });

    const raw = await apiClient.post<unknown>(
      API_ENDPOINTS.auth.switchCompany,
      { target_customer_uuid: targetCustomerUuid },
      { headers: newHeaders }
    );

    const data = unwrapAuthTokens(raw);
    persistAuthTokens(data);

    return data;
  },

  async refresh(): Promise<LoginData> {
    const refreshToken =
      tokenStorage.getRefreshTokenOptional() ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      throw new Error('Missing refresh token');
    }

    const raw = await apiClient.post<unknown>(API_ENDPOINTS.auth.refreshToken, {
      refreshToken,
      refresh_token: refreshToken,
    });

    const data = unwrapAuthTokens(raw);
    persistAuthTokens(data);

    return data;
  },
};
